import React from 'react';

import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import { camelCase } from 'lodash';

import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import IconButton from 'src/components/ui/IconButton';
import Tag from 'src/components/ui/Tag';

import { colours } from 'src/constants/colours';
import { logEvent } from 'src/utils/firebase';
import { AnalyticsEvent } from 'src/utils/analytics';
import useAlerts from 'src/hooks/useAlerts';
import useLoginModal from 'src/hooks/useLoginModal';
import useUser from 'src/hooks/useUser';
import useAppRouter from 'src/hooks/useAppRouter';

import { useAddContactMutation, useAddContentEventMutation } from 'src/hooks/__generated__/queries';
import type { User, Ad } from 'src/types/__generated__/graphql';
import { ContactStatus } from 'src/types/__generated__/graphql';

import { breakpoints } from 'src/constants/breakpoints';
import { routePathContactsWithUserId, routePathUser } from 'src/constants/router';
import { styledComponent } from 'src/utils/styled';

const StyledCard = styledComponent(Card)<{
    $margin: string;
}>`
    ${({ $margin }) => `margin: ${$margin}`};
    @media (max-width: ${breakpoints.desktopSmall - 1}px) {
        margin: 1.5rem 0rem 1.5rem 0.5rem;
    }
    .MuiCardActionArea-root {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: space-between;
    }
`;
const StyledCardContent = styledComponent(CardContent)`
    height: 100%;
    min-height: 10rem;
    position: relative;
    padding: 1.5rem 0 0 0;
    background: ${colours.white.main};
    align-items: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const StyledLowerCardContent = styledComponent(CardContent)`
    margin-top: 0.5rem;
    height: 2.875rem;
    width: 100%;
    background-color: ${colours.primary.main};
    color: ${colours.white.main};
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    padding: 1rem !important;
`;

const StyledIconButton = styledComponent(IconButton)`
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 10;
    padding: 0.25rem;
    background-color: ${colours.primaryOpacity.O08};
    color: ${colours.black.main};
    &:hover {
        background-color: ${colours.primaryOpacity.O08};
    }
    border: none !important;
`;

const StyledAvatar = styledComponent(Avatar)`
    width: 5.0625rem;
    height: 5.0625rem;
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledTypography = styledComponent(Typography)`
    font-size: 0.75rem;
    text-align: center;
`;

export interface Props {
    user: User;
    refetchQueries: string[];
    showLocation?: boolean;
    showChatButtonDirectly?: boolean;
    showActivity?: boolean;
    isOnEstimatePage?: boolean;
    ad?: Ad;
    margin?: string;
}

const CompanyTeamMemberCard: React.FC<Props> = ({
    user,
    refetchQueries,
    showLocation,
    showChatButtonDirectly,
    showActivity,
    isOnEstimatePage,
    ad,
    margin = '1.5rem 0',
}) => {
    const { t } = useTranslation(['profile']);

    const { addAlert } = useAlerts();
    const router = useAppRouter();
    const currentUser = useUser();
    const { createAuthenticatedHandler } = useLoginModal();
    const [addContentEventMutation] = useAddContentEventMutation();
    const combinedRefetchQueries = ['MyContacts', 'CurrentUserProfile', ...refetchQueries];
    const renderMutualConnections = (mutualContacts: number) =>
        t('profile:commonRelations', {
            count: mutualContacts,
            plurial: mutualContacts > 1 ? 's' : '',
        });

    const [addContactMutation] = useAddContactMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted() {
            addAlert({
                type: 'success',
                message: String(t('profile:addContactSuccess', { name: user.firstName ?? user.fullName })),
            });
        },
    });

    const canAddContact =
        !user.contactStatusV2 || [ContactStatus.Uninvited, ContactStatus.ToRespond].includes(user.contactStatusV2);
    const showAddButton = showChatButtonDirectly ? false : canAddContact;
    const addUser = async () => {
        await addContactMutation({
            variables: {
                userId: user.id,
            },
            refetchQueries: combinedRefetchQueries,
            awaitRefetchQueries: true,
        });

        logEvent(AnalyticsEvent.AddRelation);
    };
    const handleAddUser = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();
        await createAuthenticatedHandler(addUser);
    };

    const isSelf = currentUser?.id === user.id;
    const redirectChat = async () => {
        if (isOnEstimatePage) {
            await router.push(`${routePathContactsWithUserId}${user.id}&sharedAdId=${ad?.id}`);
            await addContentEventMutation({
                variables: {
                    adId: ad?.id,
                    eventType: 'shared_internally',
                },
                fetchPolicy: 'no-cache',
            });
        } else {
            await router.push(`${routePathContactsWithUserId}${user.id}`);
        }
    };
    const handleChat = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();
        await createAuthenticatedHandler(redirectChat);
    };
    return (
        <StyledCard $margin={margin} data-target-id={`company-team-member-card-${user.id}`}>
            <CardActionArea component="div" onClick={() => router.push(`${routePathUser}/${user.id}`)}>
                <StyledCardContent>
                    {!isSelf && (
                        <StyledIconButton
                            onClick={showAddButton ? handleAddUser : handleChat}
                            iconProps={{
                                name: showAddButton ? 'AddUser' : 'ChatPrimaryInactive',
                                size: '1.5rem',
                            }}
                        />
                    )}
                    <StyledAvatar src={user.image ? user.image.url : undefined} />
                    <Box width="8rem">
                        <Typography variant="body1" weight="bold" align="center" noWrap>
                            {user.fullName}
                        </Typography>
                    </Box>
                    {showActivity && (
                        <Box mb="auto" minHeight="1.125rem">
                            <StyledTypography variant="body2" color="textSecondary">
                                {t(`common:activities.${camelCase(user.activity?.labelKey)}`)}
                            </StyledTypography>
                        </Box>
                    )}
                    <Box mb="auto" minHeight="1.125rem">
                        <StyledTypography variant="body2" color="textSecondary">
                            {!isSelf &&
                                renderMutualConnections(user.mutualContactsCount ? user.mutualContactsCount : 0)}
                        </StyledTypography>
                    </Box>
                    {showLocation && (
                        <Box mt={1}>
                            <Tag size="small" label={user.city?.name} icon={<Icon name="Map" size="1rem" />} />
                        </Box>
                    )}
                </StyledCardContent>
                <StyledLowerCardContent>
                    <Box display="flex" alignItems="center" justifyContent="center">
                        <Icon name="Star" size="1rem" color="white" style={{ marginRight: '0.25rem' }} />
                        <StyledTypography color="white">
                            {`${t('profile:')}${user.averageRating ? Math.round(user.averageRating * 10) / 10 : 0} ${t(
                                'profile:ratings',
                                {
                                    count: user.numberOfRatings ? user.numberOfRatings : 0,
                                },
                            )}`}
                        </StyledTypography>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <Icon name="ThumbsUpFilled" size="1rem" style={{ marginRight: '0.25rem' }} />
                        <StyledTypography color="white">
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t('profile:recommendationCount', {
                                        count: user.recommendationsReceivedCount,
                                        plurial: user.recommendationsReceivedCount > 1 ? 's' : '',
                                    }),
                                }}
                            />
                        </StyledTypography>
                    </Box>
                </StyledLowerCardContent>
            </CardActionArea>
        </StyledCard>
    );
};

export default CompanyTeamMemberCard;
